import { QueryClient } from '@tanstack/react-query';
import { IHousingObjectResponse } from '@/types';

export const WATCHLIST_STATUS_KEY = ['watchlist-status'] as const;

type WatchlistRollback = { existed: boolean; value: boolean | undefined };

/**
 * Seed inWatchlist values from a list of objects.
 * Skips entries that are already in the cache to avoid overwriting optimistic updates.
 */
export const seedWatchlistCache = (queryClient: QueryClient, objects: IHousingObjectResponse[]) => {
  const prev = queryClient.getQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY) ?? {};
  const patch: Record<string, boolean> = {};
  for (const obj of objects) {
    const key = String(obj.id);
    if (prev[key] === undefined) {
      patch[key] = obj.inWatchlist;
    }
  }
  if (Object.keys(patch).length === 0) return;
  queryClient.setQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY, { ...prev, ...patch });
};

/** Read inWatchlist for a single object from the shared cache. Falls back to provided default. */
export const getWatchlistStatus = (
  queryClient: QueryClient,
  id: string | number,
  fallback: boolean,
): boolean => {
  const cache = queryClient.getQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY);
  return cache?.[String(id)] ?? fallback;
};

/**
 * Optimistically update a single entry in the watchlist cache.
 * Returns rollback info: whether the entry existed and its previous value.
 * Used by onMutate to enable correct rollback in onError.
 */
export const setWatchlistStatus = (
  queryClient: QueryClient,
  id: string | number,
  value: boolean,
): WatchlistRollback => {
  const prev = queryClient.getQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY);
  const key = String(id);
  const existed = prev !== undefined && key in (prev ?? {});
  const prevValue = prev?.[key];
  queryClient.setQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY, {
    ...(prev ?? {}),
    [key]: value,
  });
  return { existed, value: prevValue };
};

/**
 * Roll back a single entry after a failed mutation.
 * If the entry did not exist before the optimistic update, removes it from the cache.
 */
export const rollbackWatchlistStatus = (
  queryClient: QueryClient,
  id: string | number,
  rollback: WatchlistRollback,
) => {
  const prev = queryClient.getQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY) ?? {};
  const key = String(id);
  if (!rollback.existed) {
    const { [key]: _removed, ...rest } = prev;
    queryClient.setQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY, rest);
  } else {
    queryClient.setQueryData<Record<string, boolean>>(WATCHLIST_STATUS_KEY, {
      ...prev,
      [key]: rollback.value as boolean,
    });
  }
};
